import { mapActions, mapGetters } from 'vuex';
import checkPartner from '@/mixins/checkPartner';
import Cookies from 'js-cookie';
import { decodeSard } from '@/utils/i18nRoutes.js';
import { Capacitor } from '@capacitor/core';

export default {
  data() {
    return {
      currentlyChecking: false,
      checkUserVerificationPromise: null
    };
  },
  mixins: [checkPartner],
  computed: {
    ...mapGetters({
      externalPartnerUserToken: 'profile/externalPartner/userToken',
      userAppData: 'profile/userAppData/userAppData',
      userUpdated: 'profile/authUser/userUpdated',
      authorizationDialogShown: 'profile/dashboard/authorizationDialogShown',
      userVerificationDialog: 'profile/dashboard/dialog',
      consentList: 'cookiesConsent/consentList'
    })
  },
  methods: {
    async reloadWebUser(force) {
      if (force || this.userUpdated < new Date().getTime() - 15000) {
        await this.$auth.fetchUser();
      }
    },
    checkCookies() {
      if (this.$store.state.general.isMobileApp && !Capacitor.isNativePlatform()) {
        return true;
      }

      const cookiesConsentGranted = new Date(parseInt(Cookies.get('cookies-consent-granted')));
      const cookiesSet = this.consentList.every(item => {
        return Cookies.get(`cookie-preference_${item.identifier}`);
      });

      const cookiesConsentValid = !isNaN(cookiesConsentGranted.valueOf()) && cookiesSet;
      if (!cookiesConsentValid) {
        const params = {
          query: {
            sard: this.$buildSard({
              query: this.$route.query,
              params: this.$route.params,
              page: this.$route.name.split('___')[0]
            })
          }
        };

        this.logout({
          name: 'login',
          ...params
        });

        return false;
      }

      return true;
    },
    checkExternalPartner() {
      if (this.externalPartnerUserToken) {
        this.setCheckTokenLoading(true);
        this.setExternalRegistrationDialog(true);
        return false;
      }

      return true;
    },
    async checkUserAllowed() {
      const user = this.$auth.user;
      const isOnDashboardLock = this.$route?.name?.indexOf('dashboardLock') !== -1;

      const hasEmail = !!user?.email;
      const isEmailVerified = !!user?.verify?.email;
      const isAllowed = this.allowUser;
      const isPasswordExpired = user?.isPasswordExpired;

      if (!hasEmail || !isEmailVerified || !isAllowed || isPasswordExpired) {
        if (!isOnDashboardLock) {
          await this.$router.push(
            this.$localePath({ name: 'dashboardLock', query: this.$route.query })
          );
        }
        return false;
      }

      if (isOnDashboardLock) {
        await this.resetProfileStore();
        await this.$router.push(this.$localePath({ name: 'dashboard', query: this.$route.query }));
        return false;
      }

      return true;
    },
    async checkGoPay() {
      if (
        this.$route.query.paymentType &&
        this.$route.query.id &&
        this.$route.query.paymentType === 'gopay' &&
        this.$route.query.pd !== 'gopayThankYou'
      ) {
        await this.setGoPayId(this.$route.query.id);
        await this.setDialog({
          name: 'gopayThankYou',
          params: {
            paymentType: this.$route.query.paymentType,
            id: this.$route.query.id
          }
        });

        await this.setAuthorizationDialogShown(true);

        return false;
      }

      return true;
    },
    async checkDynamicForm() {
      if (
        this.authorizationDialogShown === false &&
        ((this.$route.query.dfn && this.$route.query.dfn.length) ||
          (this.$route.query.pd &&
            this.$route.query.pd.length &&
            this.$auth.user.authorizationType !== 'emailDomain'))
      ) {
        await this.setAuthorizationDialogShown(true);
      }
    },
    async checkB2C() {
      const isOnAccountSettings = this.$route?.name?.startsWith('accountSettings');
      if (
        !isOnAccountSettings &&
        this.authorizationDialogShown === false &&
        !this.$auth.user.benefit.active &&
        this.userAppData.findIndex(setting => setting.name === 'authorization_modal_shown') === -1
      ) {
        await this.$router.push(
          this.$localePath({ name: 'benefitManagement', query: this.$route.query })
        );

        // only set authorizationDialogShown on client, otherwise it will never be shown
        await this.setAuthorizationDialogShown(true);
        return false;
      }

      return true;
    },
    async checkReleaseNotes() {
      if (this.$i18n.locale === 'hu') {
        return true;
      }

      if (
        this.$auth.user.releaseNotesVersion !== import.meta.env.VITE_RELEASE_NOTES_VERSION &&
        !this.$route.query.dfn &&
        !this.$route.query.pd &&
        this.$i18n.locale === 'cs' &&
        this.showArticles
      ) {
        await this.setAuthorizationDialogShown(true);

        await this.setDialog('releaseNotes');

        return false;
      }

      return true;
    },
    async checkUserVerification(closeModal = true, forceReloadWebuser = false) {
      if (this.checkUserVerificationPromise) {
        return this.checkUserVerificationPromise;
      }

      this.currentlyChecking = true;
      this.checkUserVerificationPromise = (async () => {
        try {
          const isOnDashboardLock = this.$route?.name?.indexOf('dashboardLock') !== -1;

          if (isOnDashboardLock && this.$auth.isLoggedIn && !this.allowUser) {
            return true;
          }

          let checkUserVerificationResult = true;
          let currentCheck = 0;
          const checks = [
            this.checkCookies,
            this.checkExternalPartner,
            this.checkUserAllowed,
            this.checkGoPay,
            this.checkDynamicForm,
            this.checkB2C,
            this.checkReleaseNotes
          ];

          await this.reloadWebUser(forceReloadWebuser);
          this.setUserDeactivated(!!this.$auth.user?.deletedAt);
          if (this.$auth.user?.deletedAt) {
            checkUserVerificationResult = false;
          }

          while (checkUserVerificationResult !== false && currentCheck < checks.length) {
            checkUserVerificationResult = await checks[currentCheck++]();
          }

          if (checkUserVerificationResult && closeModal === true) {
            await this.setDialog(false);
          }

          if (checkUserVerificationResult && this.$route.query.sard) {
            try {
              const decodedSard = decodeSard(this.$route.query.sard);

              if (!decodedSard) {
                console.error('Decoded SARD is null or undefined');
                return checkUserVerificationResult;
              }

              const { sard, ...otherQuery } = this.$route.query;

              await this.$router.replace({
                name: this.$route.name,
                params: decodedSard.params || this.$route.params,
                query: {
                  ...decodedSard.query,
                  ...otherQuery
                }
              });
            } catch (err) {
              console.error('Failed to decode SARD:', err);
            }
          }

          if (this.$auth.isSelfReactivated) {
            await this.setDialog('selfReactivated');
          }

          return checkUserVerificationResult;
        } finally {
          this.currentlyChecking = false;
          this.checkUserVerificationPromise = null;
        }
      })();

      return this.checkUserVerificationPromise;
    },
    ...mapActions({
      setUserDeactivated: 'profile/dashboard/setUserDeactivated',
      setExternalRegistrationDialog: 'profile/externalPartner/setExternalRegistrationDialog',
      setCheckTokenLoading: 'profile/externalPartner/setCheckTokenLoading',
      setDialog: 'profile/dashboard/setDialog',
      setAuthorizationDialogShown: 'profile/dashboard/setAuthorizationDialogShown',
      resetProfileStore: 'profile/dashboard/resetProfileStore',
      setUserUpdated: 'profile/authUser/setUserUpdated',
      setGoPayId: 'profile/goPay/setGoPayId'
    })
  }
};
